import { useState } from 'react';
import { 
  Copy, 
  Check, 
  ExternalLink, 
  Sparkles, 
  Database, 
  PhoneCall
} from 'lucide-react';
import { getWhatsAppAdmissionsUrl, ADMISSIONS_PHONE, type MarketingSource } from '../../utils/whatsappLinks';

export function CrmCampaignTools() {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [customCourse, setCustomCourse] = useState('Forward Deployed Engineer (FDE)');
  const [customCampaign, setCustomCampaign] = useState('diwali-cohort-2026');

  const copyToClipboard = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2500);
  };

  const campaigns: { id: MarketingSource; title: string; tag: string; description: string }[] = [
    {
      id: 'meta-ads',
      title: 'Meta / Instagram Ads (Click-to-WhatsApp)',
      tag: '[REF:META_ADS]',
      description: 'Set this link as the destination action in Facebook & Instagram Ads Manager.',
    },
    {
      id: 'google-ads',
      title: 'Google Search & Performance Max Ads',
      tag: '[REF:GOOGLE_ADS]',
      description: 'Use in Google Ads Sitelink extensions, Callout assets, and Landing Page buttons.',
    },
    {
      id: 'youtube',
      title: 'YouTube Videos & Channel Bio',
      tag: '[REF:YOUTUBE]',
      description: 'Paste into video descriptions, pinned comments, and YouTube channel About page.',
    },
    {
      id: 'website',
      title: 'Website & Landing Page CTAs',
      tag: '[REF:WEBSITE]',
      description: 'Default organic tag for header, floating widget, and contact forms.',
    },
    {
      id: 'referral',
      title: 'Alumni & Community Referral',
      tag: '[REF:ALUMNI]',
      description: 'Share with existing students & alumni for peer-to-peer recommendation tracking.',
    },
  ];

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-slate-900/90 border border-slate-800/80 p-5 rounded-2xl shadow-lg">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-extrabold text-white tracking-tight font-heading">
              WhatsApp & Campaign Marketing Hub
            </h1>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
              Live AWS Webhook
            </span>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            Generate tracked Click-to-WhatsApp links for Meta Ads, Google Ads, and YouTube with instant CRM ingestion.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <a
            href={`https://wa.me/${ADMISSIONS_PHONE}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-bold shadow-md shadow-emerald-900/30 transition-all"
          >
            <PhoneCall className="w-4 h-4" />
            +91 9100073006
          </a>
        </div>
      </div>

      {/* Campaign Customizer Controls */}
      <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-5 shadow-lg space-y-4">
        <h2 className="text-sm font-bold text-white uppercase tracking-wider flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-indigo-400" />
          Campaign Parameters Customizer
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold text-slate-300 uppercase mb-1">Target Course / Program</label>
            <input
              type="text"
              value={customCourse}
              onChange={(e) => setCustomCourse(e.target.value)}
              className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-200 focus:outline-none focus:border-indigo-500 font-semibold"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-300 uppercase mb-1">Campaign Sub-Tag / Batch Name</label>
            <input
              type="text"
              value={customCampaign}
              onChange={(e) => setCustomCampaign(e.target.value)}
              placeholder="e.g. march-fde-batch-2026"
              className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-200 focus:outline-none focus:border-indigo-500 font-mono"
            />
          </div>
        </div>
      </div>

      {/* Generated Campaign Links List */}
      <div className="space-y-4">
        {campaigns.map((camp) => {
          const url = getWhatsAppAdmissionsUrl({
            source: camp.id,
            course: customCourse,
            campaign: customCampaign,
          });

          const isCopied = copiedKey === camp.id;

          return (
            <div
              key={camp.id}
              className="bg-slate-900/90 border border-slate-800 hover:border-slate-700 rounded-2xl p-5 shadow-lg space-y-3 transition-all"
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-white text-sm">{camp.title}</h3>
                    <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
                      {camp.tag}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">{camp.description}</p>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => copyToClipboard(url, camp.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                      isCopied 
                        ? 'bg-emerald-600 text-white' 
                        : 'bg-indigo-600 hover:bg-indigo-500 text-white'
                    }`}
                  >
                    {isCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {isCopied ? 'Copied!' : 'Copy Link'}
                  </button>

                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg transition-colors"
                    title="Test Open Link"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>

              {/* URL Display Box */}
              <div className="p-3 bg-slate-950 border border-slate-800/90 rounded-xl font-mono text-[11px] text-slate-300 break-all select-all">
                {url}
              </div>
            </div>
          );
        })}
      </div>

      {/* Live AWS Webhook Architecture Reference Card */}
      <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6 shadow-xl space-y-4">
        <h2 className="text-base font-bold text-white font-heading flex items-center gap-2">
          <Database className="w-4 h-4 text-emerald-400" />
          Live AWS Serverless Webhook Parameters
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs font-mono">
          <div className="p-3 bg-slate-950 rounded-xl border border-slate-800 space-y-1">
            <span className="text-[10px] font-bold text-slate-500 uppercase">Webhook Callback URL</span>
            <p className="text-indigo-400 font-bold break-all">
              https://5iw4lxdb2c.execute-api.ap-south-2.amazonaws.com/prod/leads
            </p>
          </div>

          <div className="p-3 bg-slate-950 rounded-xl border border-slate-800 space-y-1">
            <span className="text-[10px] font-bold text-slate-500 uppercase">Verify Token</span>
            <p className="text-emerald-400 font-bold">
              cloudbinary_wa_verify_token_2026
            </p>
          </div>

          <div className="p-3 bg-slate-950 rounded-xl border border-slate-800 space-y-1">
            <span className="text-[10px] font-bold text-slate-500 uppercase">DynamoDB Leads Table</span>
            <p className="text-slate-200 font-bold">
              CloudBinaryLMS-prod-Leads
            </p>
          </div>

          <div className="p-3 bg-slate-950 rounded-xl border border-slate-800 space-y-1">
            <span className="text-[10px] font-bold text-slate-500 uppercase">AWS Lambda Function</span>
            <p className="text-slate-200 font-bold">
              manageLeads-prod (ap-south-2)
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
